import React, { useState } from "react";

import { Box, Button, Dialog, DialogActions, DialogContent, DialogContentText, DialogTitle, LinearProgress, Modal, Paper, Stack, Typography } from "@mui/material";
import { useMutation, useQuery } from "react-query";
import { deleteProduct, fetchProducts, zeroAllProductInventory } from "../../../endpoints";
import AddProduct from "../components/AddProduct";
import { Delete, Edit, Inventory2 } from "@mui/icons-material";
import { Product } from "../../../types";
import AdminActionButton, { AdminIconButton } from "../../../components/AdminActionButton";

const AdminProducts = () => {
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [deleteConfirmationOpen, setDeleteConfirmationOpen] = useState(false);
    const [zeroInventoryOpen, setZeroInventoryOpen] = useState(false);
    const [productIdToDelete, setProductIdToDelete] = useState<string>();
    const [modalMode, setModalMode] = useState<"add" | "edit">('add');
    const [editingProduct, setEditingProduct] = useState<Product>();

    const { data: products, isLoading: isLoadingProducts, refetch: refetchProducts } = useQuery(["fetchProducts"], fetchProducts);
    const { mutate: handleDeleteProduct, isLoading: isDeletingProduct } = useMutation(deleteProduct, {
        onSuccess: refetchProducts
    });

    const { mutate: handleZeroAllInventory, isLoading: isZeroingInventory } = useMutation(
        zeroAllProductInventory,
        {
            onSuccess: () => {
                setZeroInventoryOpen(false);
                refetchProducts();
            },
        }
    );

    const handleOpenModal = (product?: Product) => {
        if (product) {
            setModalMode('edit');
            setEditingProduct(product);
        } else {
            setModalMode('add');
            setEditingProduct(undefined);
        }
        setIsModalOpen(true);
    };

    const handleCloseModal = () => setIsModalOpen(false);
    const onAddProduct = async () => {
        handleCloseModal();
        await refetchProducts();
    };

    const handleOpenDeleteConfirmation = (productId: string) => {
        setProductIdToDelete(productId);
        setDeleteConfirmationOpen(true);
    };

    const handleCloseDeleteConfirmation = () => {
        setDeleteConfirmationOpen(false);
        setProductIdToDelete(undefined);
    };

    const confirmDeleteProduct = () => {
        if (productIdToDelete) {
            handleDeleteProduct(productIdToDelete);
        }
        handleCloseDeleteConfirmation();
    };

    return (
        <Box>
            {isLoadingProducts && (
                <LinearProgress sx={{ mb: 2, borderRadius: 1 }} />
            )}

            <Stack spacing={2}>
                <Stack
                    direction={{ xs: "column", sm: "row" }}
                    alignItems={{ xs: "stretch", sm: "center" }}
                    justifyContent="space-between"
                    gap={2}
                >
                    <Typography variant="h5" component="h2" sx={{ fontWeight: 600 }}>
                        Products
                    </Typography>
                    <Stack direction={{ xs: "column", sm: "row" }} spacing={1} sx={{ alignSelf: { xs: "stretch", sm: "center" }, width: { xs: "100%", sm: "auto" } }}>
                        <AdminActionButton
                            variant="outlined"
                            color="warning"
                            loading={isZeroingInventory}
                            disabled={!products?.length}
                            onClick={() => setZeroInventoryOpen(true)}
                            startIcon={<Inventory2 fontSize="small" />}
                            sx={{ alignSelf: { xs: "stretch", sm: "center" } }}
                        >
                            Remove all inventory
                        </AdminActionButton>
                        <AdminActionButton variant="contained" onClick={() => handleOpenModal()} sx={{ alignSelf: { xs: "stretch", sm: "center" } }}>
                            Add new product
                        </AdminActionButton>
                    </Stack>
                </Stack>

                {!isLoadingProducts && products?.map((product: Product) => {
                    const youthVariations = product.variations.filter(variation => variation.category === "Youth");
                    const adultVariations = product.variations.filter(variation => variation.category === "Adult");

                    return (
                        <Paper
                            key={product._id}
                            variant="outlined"
                            sx={{ p: 2, borderRadius: 2 }}
                        >
                            <Stack
                                direction={{ xs: "column", md: "row" }}
                                spacing={2}
                                alignItems={{ xs: "flex-start", md: "center" }}
                            >
                                <Stack direction="row" flexWrap="wrap" gap={0.5} useFlexGap>
                                    {product.images.map((imageSrc, imageIndex) => (
                                        <img
                                            key={imageSrc}
                                            height={72}
                                            width={72}
                                            src={imageSrc}
                                            alt={`${product.name}-${imageIndex}`}
                                            style={{ objectFit: "cover", borderRadius: 8 }}
                                        />
                                    ))}
                                </Stack>
                                <Box flex={1} minWidth={0}>
                                    <Typography fontWeight={600} variant="subtitle1" gutterBottom>
                                        {product.name}
                                    </Typography>
                                    <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                                        Youth: ${product.priceYouth}
                                        {" · "}
                                        In stock:{" "}
                                        {youthVariations.length
                                            ? youthVariations.map((variation) => (
                                                <span key={`${variation.category}-${variation.size}`}>
                                                    {variation.size}: {variation.inventory}{" "}
                                                </span>
                                            ))
                                            : "no Youth sizes in stock"}
                                    </Typography>
                                    <Typography variant="body2" color="text.secondary">
                                        Adult: ${product.priceAdult}
                                        {" · "}
                                        In stock:{" "}
                                        {adultVariations.length
                                            ? adultVariations.map((variation) => (
                                                <span key={`${variation.category}-${variation.size}`}>
                                                    {variation.size}: {variation.inventory}{" "}
                                                </span>
                                            ))
                                            : "no Adult sizes in stock"}
                                    </Typography>
                                </Box>
                                <Stack direction="row" spacing={1} sx={{ alignSelf: { xs: "stretch", md: "center" }, ml: { md: "auto" } }}>
                                    <AdminActionButton variant="outlined" size="small" onClick={() => handleOpenModal(product)} startIcon={<Edit fontSize="small" />}>
                                        Edit
                                    </AdminActionButton>
                                    <AdminIconButton
                                        loading={isDeletingProduct}
                                        aria-label="Delete product"
                                        color="primary"
                                        onClick={() => handleOpenDeleteConfirmation(product._id)}
                                        size="small"
                                    >
                                        <Delete />
                                    </AdminIconButton>
                                </Stack>
                            </Stack>
                        </Paper>
                    )
                })}

                {!isLoadingProducts && !products?.length && (
                    <Typography color="text.secondary">No products yet. Add one to get started.</Typography>
                )}
            </Stack>

            <Modal
                open={isModalOpen}
                onClose={handleCloseModal}
                aria-labelledby="add-product-modal-title"
                aria-describedby="add-product-modal-description"
                sx={{ display: "flex", alignItems: "center", justifyContent: "center", p: 2 }}
            >
                <Box
                    borderRadius={2}
                    p={2}
                    width={1000}
                    maxWidth="100%"
                    maxHeight="90vh"
                    overflow="auto"
                    sx={{ bgcolor: "background.paper", boxShadow: 24 }}
                >
                    <AddProduct mode={modalMode} product={editingProduct} onSuccess={onAddProduct} />
                </Box>
            </Modal>
            <Dialog
                open={deleteConfirmationOpen}
                onClose={handleCloseDeleteConfirmation}
                aria-labelledby="delete-confirmation-dialog-title"
                aria-describedby="delete-confirmation-dialog-description"
            >
                <DialogTitle id="delete-confirmation-dialog-title">Confirm deletion</DialogTitle>
                <DialogContent>
                    <DialogContentText id="delete-confirmation-dialog-description">
                        Are you sure you want to delete this product? This action cannot be undone.
                    </DialogContentText>
                </DialogContent>
                <DialogActions sx={{ px: 3, pb: 2 }}>
                    <Button onClick={handleCloseDeleteConfirmation}>Cancel</Button>
                    <Button onClick={confirmDeleteProduct} color="error" variant="contained" autoFocus>
                        Delete
                    </Button>
                </DialogActions>
            </Dialog>

            <Dialog
                open={zeroInventoryOpen}
                onClose={() => !isZeroingInventory && setZeroInventoryOpen(false)}
                aria-labelledby="zero-inventory-dialog-title"
                aria-describedby="zero-inventory-dialog-description"
            >
                <DialogTitle id="zero-inventory-dialog-title">Remove all inventory?</DialogTitle>
                <DialogContent>
                    <DialogContentText id="zero-inventory-dialog-description">
                        This sets stock to <strong>0</strong> for every size on every product. Products stay in the catalog;
                        you can edit quantities again later. This cannot be undone automatically (restore stock manually or from a backup).
                    </DialogContentText>
                </DialogContent>
                <DialogActions sx={{ px: 3, pb: 2 }}>
                    <Button onClick={() => setZeroInventoryOpen(false)} disabled={isZeroingInventory}>
                        Cancel
                    </Button>
                    <Button
                        onClick={() => handleZeroAllInventory()}
                        color="warning"
                        variant="contained"
                        disabled={isZeroingInventory}
                        autoFocus
                    >
                        Remove all inventory
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    )
};

export default AdminProducts;
